import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useId } from "react";
import { FormattedMessage as T } from "react-intl";
import useGlobalContext from "hooks/globalData";
import { useQuery } from "react-query";
import { SkuAvailabilityId } from "types/commonTypes";
import { CatalogFiltersRequest } from "services/organizationPortalClient";
import contextStore from "stores/contextStore";

export type SelectMultipleGenericFilterProps = {
  typeId: string;
  labelKey: string;
  skuAvailabilityIds?: SkuAvailabilityId[];
  value?: string[];
  onChange: (value: string[]) => void;
};

export default function SelectMultipleGenericFilter({
  typeId,
  labelKey,
  skuAvailabilityIds,
  value,
  onChange,
}: SelectMultipleGenericFilterProps) {
  const { catalogFilterService } = useGlobalContext();
  const { organization } = contextStore.useState();

  // MUI names the select from labelId, not from an aria-labelledby prop.
  const labelId = useId();

  const { data, isLoading, isFetched } = useQuery(
    ["getFilters", organization?.id, typeId, ...(skuAvailabilityIds ?? [])],
    () =>
      catalogFilterService.getFilters(
        CatalogFiltersRequest.fromJS({
          skuAvailabilityIds,
          typeId,
        })
      )
  );

  const options = data?.data ?? [];

  if (isFetched && options.length < 2) {
    return null;
  }

  return (
    <Grid item xs={12} sm={12} md={4}>
      <FormControl size="small" fullWidth>
        <InputLabel id={labelId} sx={{ background: "white" }}>
          <T id={labelKey} />
        </InputLabel>
        <Select
          labelId={labelId}
          multiple
          value={value ?? []}
          onChange={(e) => onChange(e.target.value as string[])}
          endAdornment={isLoading ? <CircularProgress size={16} /> : undefined}
          renderValue={(selected: string[]) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected?.map((id: string) => (
                <Chip
                  key={id}
                  size="small"
                  label={options?.find((p) => p.id === id)?.value}
                />
              ))}
            </Box>
          )}
        >
          {options?.map((p) => (
            <MenuItem dense key={p.id} value={p.id}>
              {p.value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
}
